/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import axios from "axios";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import Autocomplete from "@mui/material/Autocomplete";
import FileSaver from "file-saver";
import swal from "sweetalert";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Data
import { API_HOHOLOK, HOHOLOK_UPLOADS } from "utils";

function ProyekList() {
  const [menu, setMenu] = useState(null);
  const [proyekList, setProyekList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form, setForm] = useState({
    nama_proyek: "",
    tanggal_mulai: "",
    tanggal_selesai: "",
    total_anggaran: "",
    desa: "",
    kecamatan: "",
    longitude: "",
    latitude: "",
    radiusInput: "",
    konsultan_perencana: null,
    konsultan_pengawas: null,
    kontraktor_pelaksana: null,
    akunKontraktor: null,
    akunPengawas: null,
  });
  const [editId, setEditId] = useState(null);
  const [filterStatus, setFilterStatus] = useState("All");
  const [search, setSearch] = useState("");
  const statusOptions = ["All", "Aktif", "Selesai", "Belum Mulai"];
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  // State untuk data perusahaan dan user
  const [kontraktorOptions, setKontraktorOptions] = useState([]);
  const [konsultanPengawasOptions, setKonsultanPengawasOptions] = useState([]);
  const [konsultanPerencanaOptions, setKonsultanPerencanaOptions] = useState(
    []
  );
  const [akunKontraktorUserOptions, setAkunKontraktorUserOptions] = useState(
    []
  );
  const [akunPengawasUserOptions, setAkunPengawasUserOptions] = useState([]);

  // State untuk data desa dan kecamatan
  const [desaList, setDesaList] = useState([]);
  const [kecamatanOptions, setKecamatanOptions] = useState([]);
  const [desaOptions, setDesaOptions] = useState([]);
  const [loadingDesa, setLoadingDesa] = useState(true);

  const fetchProyek = () => {
    setLoading(true);
    // Ambil user dari localStorage untuk mendapatkan id_dinas
    const userStorage = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null;
    const idDinas =
      userStorage && userStorage.id_dinas ? userStorage.id_dinas : 7;
    const tahun = new Date().getFullYear();

    axios
      .get(`${API_HOHOLOK}proyek/tahun/${tahun}/dinas/${idDinas}`)
      .then((res) => {
        setProyekList(res.data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching proyek:", err);
        setProyekList([]);
        setLoading(false);
      });
  };

  // Fetch data perusahaan
  const fetchPerusahaan = () => {
    axios
      .get(`${API_HOHOLOK}perusahaan`)
      .then((res) => {
        const data = res.data || [];

        setKontraktorOptions(
          data
            .filter((item) => item.jenis_perusahaan === "Kontraktor")
            .map((item) => ({
              label: item.nama_perusahaan,
              value: item.id_perusahaan,
            }))
        );
        setKonsultanPengawasOptions(
          data
            .filter((item) => item.jenis_perusahaan === "Konsultan Pengawas")
            .map((item) => ({
              label: item.nama_perusahaan,
              value: item.id_perusahaan,
            }))
        );
        setKonsultanPerencanaOptions(
          data
            .filter((item) => item.jenis_perusahaan === "Konsultan Perencana")
            .map((item) => ({
              label: item.nama_perusahaan,
              value: item.id_perusahaan,
            }))
        );
      })
      .catch(() => {
        setKontraktorOptions([]);
        setKonsultanPengawasOptions([]);
        setKonsultanPerencanaOptions([]);
      });
  };

  // Fetch users berdasarkan perusahaan
  const fetchUsers = (perusahaanId, setUserOptions) => {
    if (!perusahaanId) {
      setUserOptions([]);
      return;
    }

    axios
      .get(`${API_HOHOLOK}users/perusahaan/${perusahaanId}`)
      .then((res) => {
        const options = (res.data || []).map((data) => ({
          label: data.nama_user ? data.nama_user.split(" ")[0] : "-",
          value: data.id_user,
          avatar: data.profil ? `${HOHOLOK_UPLOADS}${data.profil}` : undefined,
        }));
        setUserOptions(options);
      })
      .catch((err) => {
        setUserOptions([]);
      });
  };

  // Fetch data desa dari endpoint
  const fetchDesa = () => {
    setLoadingDesa(true);
    axios
      .get(`${API_HOHOLOK}desa/`)
      .then((res) => {
        setDesaList(res.data);
        const kecamatanSet = Array.from(
          new Set(res.data.map((item) => item.kecamatan))
        );
        setKecamatanOptions(kecamatanSet);
        setLoadingDesa(false);
      })
      .catch((err) => {
        setDesaList([]);
        setKecamatanOptions([]);
        setLoadingDesa(false);
      });
  };

  useEffect(() => {
    fetchProyek();
    fetchPerusahaan();
    fetchDesa();
  }, []);

  // Fetch users ketika kontraktor_pelaksana berubah
  useEffect(() => {
    fetchUsers(form.kontraktor_pelaksana, setAkunKontraktorUserOptions);
  }, [form.kontraktor_pelaksana]);

  // Fetch users ketika konsultan_pengawas berubah
  useEffect(() => {
    fetchUsers(form.konsultan_pengawas, setAkunPengawasUserOptions);
  }, [form.konsultan_pengawas]);

  // Update desaOptions jika kecamatan berubah
  useEffect(() => {
    if (form.kecamatan) {
      const filtered = desaList
        .filter((item) => item.kecamatan === form.kecamatan)
        .map((item) => item.desa);
      setDesaOptions(filtered);
    } else {
      setDesaOptions([]);
    }
  }, [form.kecamatan, desaList]);

  // Fungsi format rupiah
  function formatRupiah(angka) {
    if (!angka) return "Rp 0";
    const numberString = angka.toString().replace(/[^\d]/g, "");
    const sisa = numberString.length % 3;
    let rupiah = numberString.substr(0, sisa);
    const ribuan = numberString.substr(sisa).match(/\d{3}/g);
    if (ribuan) {
      rupiah += (sisa ? "." : "") + ribuan.join(".");
    }
    return rupiah ? "Rp " + rupiah : "Rp 0";
  }

  // Fungsi format tanggal
  function formatTanggal(tanggal) {
    if (!tanggal) return "-";
    return new Date(tanggal).toLocaleDateString("id-ID", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  // Fungsi untuk mendapatkan icon berdasarkan ekstensi file
  const getFileIcon = (filename) => {
    if (!filename) return <InsertDriveFileIcon />;

    const extension = filename.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "pdf":
        return <PictureAsPdfIcon color="error" />;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "bmp":
      case "svg":
        return <ImageIcon color="primary" />;
      case "doc":
      case "docx":
      case "xls":
      case "xlsx":
      case "ppt":
      case "pptx":
      case "txt":
        return <DescriptionIcon color="info" />;
      default:
        return <InsertDriveFileIcon color="action" />;
    }
  };

  // Fungsi untuk menangani klik pada file
  const handleFileClick = (filePath) => {
    if (!filePath) return;

    const fullUrl = `${HOHOLOK_UPLOADS}${filePath}`;
    const extension = filePath.split(".").pop()?.toLowerCase();

    if (extension === "pdf") {
      // Buka PDF di tab baru
      window.open(fullUrl, "_blank");
    } else {
      // Download file untuk tipe lain
      const link = document.createElement("a");
      link.href = fullUrl;
      link.download = filePath.split("/").pop();
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Definisikan kolom DataTable
  const columns = [
    {
      Header: "No",
      accessor: "nomor",
      Cell: ({ row }) => row.index + 1,
      width: 50,
    },
    { Header: "Nama Proyek", accessor: "nama_proyek" },
    { Header: "Lokasi", accessor: "lokasi" },
    { Header: "Tanggal Mulai", accessor: "tanggal_mulai" },
    { Header: "Tanggal Selesai", accessor: "tanggal_selesai" },
    { Header: "Total Anggaran", accessor: "total_anggaran" },
    { Header: "Kontraktor", accessor: "nama_kontraktor_pelaksana" },
    { Header: "Konsultan Perencana", accessor: "nama_konsultan_perencana" },
    { Header: "Konsultan Pengawas", accessor: "nama_konsultan_pengawas" },
    { Header: "Akun Kontraktor", accessor: "nama_akun_kontraktor" },
    { Header: "Akun Pengawas", accessor: "nama_akun_pengawas" },

    {
      Header: "Dokumen",
      accessor: "dokumen",
      Cell: ({ value }) => {
        if (!value || value === "-") return "-";

        try {
          const dokumenArray = JSON.parse(value);
          if (!Array.isArray(dokumenArray) || dokumenArray.length === 0)
            return "-";

          return (
            <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
              {dokumenArray.map((file, index) => (
                <IconButton
                  key={index}
                  size="small"
                  onClick={() => handleFileClick(file)}
                  title={file.split("/").pop()}
                  sx={{ padding: "2px" }}
                >
                  {getFileIcon(file)}
                </IconButton>
              ))}
            </div>
          );
        } catch (error) {
          return "-";
        }
      },
    },
    { Header: "Longitude", accessor: "longitude" },
    { Header: "Latitude", accessor: "latitude" },
    { Header: "Radius Input", accessor: "radiusInput" },
    { Header: "Admin", accessor: "nama_admin_proyek" },
    {
      Header: "Aksi",
      accessor: "aksi",
      Cell: ({ row }) => (
        <div style={{ display: "flex", gap: "4px" }}>
          <IconButton
            color="info"
            onClick={() => handleEdit(row.original)}
            size="small"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => handleDelete(row.original.id_proyek)}
            size="small"
          >
            <DeleteIcon />
          </IconButton>
        </div>
      ),
    },
  ];

  // Data baris, filter sesuai filterStatus dan search
  const filteredRows = proyekList
    .filter((item) => {
      if (filterStatus === "All") return true;
      const today = new Date();
      const tanggalMulai = new Date(item.tanggal_mulai);
      const tanggalSelesai = new Date(item.tanggal_selesai);

      if (filterStatus === "Aktif") {
        return today >= tanggalMulai && today <= tanggalSelesai;
      } else if (filterStatus === "Selesai") {
        return today > tanggalSelesai;
      } else if (filterStatus === "Belum Mulai") {
        return today < tanggalMulai;
      }
      return true;
    })
    .filter((item) =>
      item.nama_proyek.toLowerCase().includes(search.trim().toLowerCase())
    )
    .map((item) => ({
      ...item,
      tanggal_mulai: formatTanggal(item.tanggal_mulai),
      tanggal_selesai: formatTanggal(item.tanggal_selesai),
      total_anggaran: formatRupiah(item.total_anggaran),
      lokasi: item.lokasi || "-",
      nama_kontraktor_pelaksana: item.nama_kontraktor_pelaksana || "-",
      nama_konsultan_perencana: item.nama_konsultan_perencana || "-",
      nama_konsultan_pengawas: item.nama_konsultan_pengawas || "-",
      nama_akun_kontraktor: item.nama_akun_kontraktor || "-",
      nama_akun_pengawas: item.nama_akun_pengawas || "-",
      createdAt: formatTanggal(item.createdAt),
      dokumen: item.dokumen || "[]",
      longitude: item.longitude || "-",
      latitude: item.latitude || "-",
      radiusInput: item.radiusInput ? `${item.radiusInput} meter` : "-",
      nama_admin_proyek: item.nama_admin_proyek || "-",
    }));

  const handleOpenDialog = () => {
    setForm({
      nama_proyek: "",
      tanggal_mulai: "",
      tanggal_selesai: "",
      total_anggaran: "",
      desa: "",
      kecamatan: "",
      longitude: "",
      latitude: "",
      radiusInput: "",
      konsultan_perencana: null,
      konsultan_pengawas: null,
      kontraktor_pelaksana: null,
      akunKontraktor: null,
      akunPengawas: null,
    });
    setIsEdit(false);
    setEditId(null);
    setOpenDialog(true);
  };

  const handleEdit = (data) => {
    // Cari data asli dari proyekList berdasarkan id_proyek
    const originalData = proyekList.find(
      (item) => item.id_proyek === data.id_proyek
    );
    if (!originalData) {
      console.error("Data asli tidak ditemukan");
      return;
    }

    // Cari kecamatan berdasarkan lokasi (desa) dari data desaList
    let kecamatanFromLokasi = "";
    if (originalData.lokasi && desaList.length > 0) {
      const desaData = desaList.find(
        (item) => item.desa === originalData.lokasi
      );
      if (desaData) {
        kecamatanFromLokasi = desaData.kecamatan;
        // Set desaOptions untuk kecamatan yang ditemukan
        const filtered = desaList
          .filter((item) => item.kecamatan === kecamatanFromLokasi)
          .map((item) => item.desa);
        setDesaOptions(filtered);
      }
    }

    setForm({
      nama_proyek: originalData.nama_proyek || "",
      tanggal_mulai: originalData.tanggal_mulai
        ? new Date(originalData.tanggal_mulai).toISOString().split("T")[0]
        : "",
      tanggal_selesai: originalData.tanggal_selesai
        ? new Date(originalData.tanggal_selesai).toISOString().split("T")[0]
        : "",
      total_anggaran: originalData.total_anggaran
        ? originalData.total_anggaran.toString()
        : "",
      desa: originalData.lokasi || "", // Gunakan lokasi sebagai desa
      kecamatan: kecamatanFromLokasi || "", // Gunakan kecamatan yang ditemukan
      longitude: originalData.longitude
        ? originalData.longitude.toString()
        : "",
      latitude: originalData.latitude ? originalData.latitude.toString() : "",
      radiusInput: originalData.radiusInput
        ? originalData.radiusInput.toString()
        : "",
      konsultan_perencana: originalData.id_konsultan_perencana || null,
      konsultan_pengawas: originalData.id_konsultan_pengawas || null,
      kontraktor_pelaksana: originalData.id_kontraktor_pelaksana || null,
      akunKontraktor: originalData.id_akunKontraktor || null,
      akunPengawas: originalData.id_akunPengawas || null,
    });
    setIsEdit(true);
    setEditId(originalData.id_proyek);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setIsEdit(false);
    setEditId(null);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleDelete = async (id) => {
    const result = await swal({
      title: "Apakah Anda yakin?",
      text: "Data proyek akan dihapus secara permanen!",
      icon: "warning",
      buttons: ["Batal", "Hapus"],
      dangerMode: true,
    });

    if (result) {
      try {
        await axios.delete(`${API_HOHOLOK}proyek/${id}`);
        swal("Sukses", "Data proyek berhasil dihapus!", "success");
        fetchProyek();
      } catch (err) {
        swal("Gagal", "Gagal menghapus data proyek", "error");
      }
    }
  };

  const handleSubmit = async () => {
    try {
      setLoadingSubmit(true);

      // Validasi field wajib
      const requiredFields = [
        "nama_proyek",
        "tanggal_mulai",
        "tanggal_selesai",
        "total_anggaran",
        "desa",
        "kecamatan",
        "longitude",
        "latitude",
        "radiusInput",
        "konsultan_perencana",
        "konsultan_pengawas",
        "kontraktor_pelaksana",
        "akunKontraktor",
        "akunPengawas",
      ];

      const emptyFields = [];
      requiredFields.forEach((field) => {
        if (!form[field] || form[field] === "") {
          emptyFields.push(field);
        }
      });

      if (emptyFields.length > 0) {
        swal(
          "Gagal",
          `Field berikut wajib diisi: ${emptyFields.join(", ")}`,
          "error"
        );
        setLoadingSubmit(false);
        return;
      }

      // Format data sesuai dengan format POST
      const dataToSend = {
        nama_proyek: form.nama_proyek,
        tanggal_mulai: form.tanggal_mulai,
        tanggal_selesai: form.tanggal_selesai,
        total_anggaran: form.total_anggaran
          ? Number(form.total_anggaran)
          : null,
        lokasi: form.desa,

        longitude: form.longitude ? Number(form.longitude) : null,
        latitude: form.latitude ? Number(form.latitude) : null,
        radiusInput: form.radiusInput ? Number(form.radiusInput) : null,
        id_konsultan_perencana: form.konsultan_perencana,
        id_konsultan_pengawas: form.konsultan_pengawas,
        id_kontraktor_pelaksana: form.kontraktor_pelaksana,
        id_akunKontraktor: form.akunKontraktor,
        id_akunPengawas: form.akunPengawas,
      };

      if (isEdit && editId) {
        await axios.put(`${API_HOHOLOK}proyek/${editId}`, dataToSend);
        swal("Sukses", "Data proyek berhasil diperbarui!", "success");
      } else {
        // Ambil user dari localStorage untuk POST
        const userStorage = localStorage.getItem("user")
          ? JSON.parse(localStorage.getItem("user"))
          : null;
        const pengirim_id =
          userStorage && userStorage.uid ? userStorage.uid : null;
        const idDinas =
          userStorage && userStorage.id_dinas ? userStorage.id_dinas : null;

        dataToSend.id_admin = pengirim_id;
        dataToSend.id_dinas = idDinas;

        await axios.post(`${API_HOHOLOK}proyek`, dataToSend);
        swal("Sukses", "Data proyek berhasil ditambahkan!", "success");
      }

      handleCloseDialog();
      fetchProyek();
    } catch (err) {
      swal("Gagal", "Gagal menyimpan data proyek", "error");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const openMenu = (event) => setMenu(event.currentTarget);
  const closeMenu = () => setMenu(null);

  const renderMenu = (
    <Menu
      anchorEl={menu}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      open={Boolean(menu)}
      onClose={closeMenu}
      keepMounted
    >
      <MenuItem
        selected={filterStatus === "All"}
        onClick={() => {
          setFilterStatus("All");
          closeMenu();
        }}
      >
        All
      </MenuItem>
      {statusOptions.slice(1).map((status) => (
        <MenuItem
          key={status}
          selected={filterStatus === status}
          onClick={() => {
            setFilterStatus(status);
            closeMenu();
          }}
        >
          {status}
        </MenuItem>
      ))}
    </Menu>
  );

  // Fungsi export CSV
  const handleExportCSV = () => {
    if (!filteredRows.length) return;
    const header = [
      "No",
      "Nama Proyek",
      "Lokasi",
      "Tanggal Mulai",
      "Tanggal Selesai",
      "Total Anggaran",
      "Kontraktor",
      "Konsultan Perencana",
      "Konsultan Pengawas",
      "Akun Kontraktor",
      "Akun Pengawas",
      "Created At",
      "Dokumen",
      "Longitude",
      "Latitude",
      "Radius Input",
      "Admin",
    ];
    const csvRows = [
      header.join(","),
      ...filteredRows.map((row, index) => {
        // Format dokumen untuk CSV
        let dokumenText = "-";
        try {
          const dokumenArray = JSON.parse(row.dokumen);
          if (Array.isArray(dokumenArray) && dokumenArray.length > 0) {
            dokumenText = dokumenArray
              .map((file) => `${HOHOLOK_UPLOADS}${file}`)
              .join("; ");
          }
        } catch (error) {
          dokumenText = "-";
        }

        return [
          index + 1,
          row.nama_proyek,
          row.lokasi,
          row.tanggal_mulai,
          row.tanggal_selesai,
          row.total_anggaran,
          row.nama_kontraktor_pelaksana,
          row.nama_konsultan_perencana,
          row.nama_konsultan_pengawas,
          row.nama_akun_kontraktor,
          row.nama_akun_pengawas,
          row.createdAt,
          dokumenText,
          row.longitude,
          row.latitude,
          row.radiusInput,
          row.nama_admin_proyek,
        ]
          .map((v) => `"${(v || "").replace(/"/g, '""')}"`)
          .join(",");
      }),
    ];
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    FileSaver.saveAs(blob, "proyek.csv");
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={2}
        >
          <MDButton variant="gradient" color="info" onClick={handleOpenDialog}>
            Tambah Proyek
          </MDButton>
          <MDBox display="flex">
            <MDButton
              variant={menu ? "contained" : "outlined"}
              color="dark"
              onClick={openMenu}
            >
              filters&nbsp;
              <Icon>keyboard_arrow_down</Icon>
            </MDButton>
            {renderMenu}
            <MDBox ml={1}>
              <MDButton
                variant="outlined"
                color="dark"
                onClick={handleExportCSV}
              >
                <Icon>description</Icon>
                &nbsp;Export CSV
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
        <Card>
          <DataTable
            table={{ columns, rows: filteredRows }}
            entriesPerPage={false}
            canSearch
            isLoading={loading}
          />
        </Card>
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{isEdit ? "Edit Proyek" : "Tambah Proyek"}</DialogTitle>
          <DialogContent dividers>
            {loadingDesa ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                <p>Loading data...</p>
              </div>
            ) : (
              <>
                <TextField
                  margin="dense"
                  label="Nama Proyek"
                  name="nama_proyek"
                  value={form.nama_proyek}
                  onChange={handleChange}
                  fullWidth
                  required
                />
                <TextField
                  margin="dense"
                  label="Tanggal Mulai"
                  name="tanggal_mulai"
                  type="date"
                  value={form.tanggal_mulai}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  margin="dense"
                  label="Tanggal Selesai"
                  name="tanggal_selesai"
                  type="date"
                  value={form.tanggal_selesai}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  margin="dense"
                  label="Total Anggaran (Rupiah)"
                  name="total_anggaran"
                  type="number"
                  value={form.total_anggaran}
                  onChange={handleChange}
                  fullWidth
                  required
                  inputProps={{ min: 0, step: 1 }}
                />
                <TextField
                  margin="dense"
                  label="Longitude"
                  name="longitude"
                  type="number"
                  value={form.longitude}
                  onChange={handleChange}
                  fullWidth
                  inputProps={{ step: "any" }}
                />
                <TextField
                  margin="dense"
                  label="Latitude"
                  name="latitude"
                  type="number"
                  value={form.latitude}
                  onChange={handleChange}
                  fullWidth
                  inputProps={{ step: "any" }}
                />
                <TextField
                  margin="dense"
                  label="Radius Input (meter)"
                  name="radiusInput"
                  type="number"
                  value={form.radiusInput}
                  onChange={handleChange}
                  fullWidth
                  inputProps={{ min: 0, step: 1 }}
                />
                <Autocomplete
                  options={kecamatanOptions}
                  value={form.kecamatan || ""}
                  onChange={(event, newValue) => {
                    setForm({ ...form, kecamatan: newValue || "", desa: "" });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Kecamatan"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  disabled={loadingDesa}
                />
                <Autocomplete
                  options={desaOptions}
                  value={form.desa || ""}
                  onChange={(event, newValue) => {
                    setForm({ ...form, desa: newValue || "" });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Desa/Kelurahan"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  disabled={!form.kecamatan}
                />
                <Autocomplete
                  options={konsultanPerencanaOptions}
                  value={
                    konsultanPerencanaOptions.find(
                      (opt) => opt.value === form.konsultan_perencana
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setForm({
                      ...form,
                      konsultan_perencana: newValue ? newValue.value : null,
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Konsultan Perencana"
                      fullWidth
                    />
                  )}
                />
                <Autocomplete
                  options={konsultanPengawasOptions}
                  value={
                    konsultanPengawasOptions.find(
                      (opt) => opt.value === form.konsultan_pengawas
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setForm({
                      ...form,
                      konsultan_pengawas: newValue ? newValue.value : null,
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Konsultan Pengawas"
                      fullWidth
                    />
                  )}
                />
                <Autocomplete
                  options={kontraktorOptions}
                  value={
                    kontraktorOptions.find(
                      (opt) => opt.value === form.kontraktor_pelaksana
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setForm({
                      ...form,
                      kontraktor_pelaksana: newValue ? newValue.value : null,
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Kontraktor Pelaksana"
                      fullWidth
                    />
                  )}
                />
                <Autocomplete
                  options={akunKontraktorUserOptions}
                  value={
                    akunKontraktorUserOptions.find(
                      (opt) => opt.value === form.akunKontraktor
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setForm({
                      ...form,
                      akunKontraktor: newValue ? newValue.value : null,
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Akun Kontraktor"
                      fullWidth
                    />
                  )}
                />
                <Autocomplete
                  options={akunPengawasUserOptions}
                  value={
                    akunPengawasUserOptions.find(
                      (opt) => opt.value === form.akunPengawas
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setForm({
                      ...form,
                      akunPengawas: newValue ? newValue.value : null,
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="dense"
                      label="Akun Pengawas"
                      fullWidth
                    />
                  )}
                />
              </>
            )}
          </DialogContent>
          <DialogActions>
            <MDButton
              color="secondary"
              onClick={handleCloseDialog}
              disabled={loadingSubmit}
            >
              Batal
            </MDButton>
            <MDButton
              color="info"
              variant="contained"
              onClick={handleSubmit}
              disabled={loadingDesa || loadingSubmit}
              startIcon={loadingSubmit ? <Icon>hourglass_empty</Icon> : null}
            >
              {loadingSubmit ? "Menyimpan..." : "Simpan"}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ProyekList;
